import { useRef, useState } from 'react';
import { FilePlus2, PencilLine, PartyPopper } from 'lucide-react';
import { STEPS, stepNumber } from '@/steps';
import StepCard from '@/components/StepCard';

const MODES = [
  { value: 'new', label: 'New Xero Setup', hint: "Setting up a client's Xero organisation for the first time", Icon: FilePlus2 },
  { value: 'amend', label: 'Amending Existing Setup', hint: 'Changing an organisation that has already been set up', Icon: PencilLine },
];

// Nothing is saved anywhere: reloading the page starts a fresh checklist (the sheet's old "reset" step).
// `hidden` while Settings is open (/settings): the checklist stays mounted so its progress isn't lost.
export default function Checklist({ hidden }) {
  const [mode, setMode] = useState(null);
  const [values, setValues] = useState(() => Object.fromEntries(STEPS.map((s) => [s.id, {}])));
  const [status, setStatus] = useState({});          // stepId -> 'completed' (missing = pending)
  const [completedBy, setCompletedBy] = useState({}); // stepId -> 'processed' | 'manual'
  const [processing, setProcessing] = useState(null);
  const [notices, setNotices] = useState({});
  const stepRefs = useRef([]);

  const completedCount = STEPS.filter((s) => status[s.id] === 'completed').length;

  // A step opens once a setup type is chosen and the step before it is Completed.
  const isLocked = (i) => !mode || (i > 0 && status[STEPS[i - 1].id] !== 'completed');
  const lockedReason = (i) =>
    !mode ? 'Choose New Xero Setup or Amending Existing Setup above to begin' : `Opens once Step ${stepNumber(i - 1)} is completed`;

  const scrollToStep = (i) => stepRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const complete = (i, how) => {
    const id = STEPS[i].id;
    setStatus((s) => ({ ...s, [id]: 'completed' }));
    setCompletedBy((c) => ({ ...c, [id]: how }));
    setNotices((n) => ({ ...n, [id]: null }));
    if (i + 1 < STEPS.length) setTimeout(() => scrollToStep(i + 1), 250);
  };

  const undo = (i) => {
    const id = STEPS[i].id;
    setStatus((s) => ({ ...s, [id]: undefined }));
    setCompletedBy((c) => ({ ...c, [id]: undefined }));
  };

  const setField = (stepId, name, value) =>
    setValues((v) => ({ ...v, [stepId]: { ...v[stepId], [name]: value } }));

  // Each step's Xero API call goes here as it is built. Until then Process says so and the step stays Pending.
  const processStep = async (i) => {
    const id = STEPS[i].id;
    setProcessing(id);
    setNotices((n) => ({ ...n, [id]: null }));
    await new Promise((r) => setTimeout(r, 700));
    setProcessing(null);
    setNotices((n) => ({
      ...n,
      [id]: "This step isn't connected to Xero yet. If it isn't needed, use \"Mark as completed\" to move on.",
    }));
  };

  return (
    <div hidden={hidden}>
      <fieldset className="xs-mode">
        <legend className="xs-mode-legend">What are you doing today?</legend>
        <div className="xs-mode-options">
          {MODES.map(({ value, label, hint, Icon }) => (
            <label key={value} className={`xs-mode-option${mode === value ? ' is-selected' : ''}`}>
              <input type="radio" name="mode" value={value} checked={mode === value} onChange={() => setMode(value)} />
              <Icon size={26} className="xs-mode-icon" />
              <span>
                <span className="xs-mode-label">{label}</span>
                <span className="xs-mode-hint">{hint}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      {/* Stays at the top while scrolling: shows progress and jumps to any step. */}
      <nav className="xs-progress" aria-label="Steps">
        <div className="xs-progress-text">
          <strong>{completedCount}</strong> of {STEPS.length} completed
        </div>
        <div className="xs-progress-bar"><div style={{ width: `${(completedCount / STEPS.length) * 100}%` }} /></div>
        <ol className="xs-chips">
          {STEPS.map((s, i) => {
            const state = status[s.id] === 'completed' ? 'completed' : isLocked(i) ? 'locked' : 'active';
            return (
              <li key={s.id}>
                <button type="button" className={`xs-chip is-${state}`} onClick={() => scrollToStep(i)} title={s.title}>
                  {stepNumber(i)}
                </button>
              </li>
            );
          })}
        </ol>
      </nav>

      <div className="xs-steps">
        {STEPS.map((step, i) => (
          <StepCard
            key={step.id}
            ref={(el) => { stepRefs.current[i] = el; }}
            step={step}
            index={i}
            values={values[step.id]}
            status={status[step.id]}
            completedBy={completedBy[step.id]}
            locked={isLocked(i)}
            lockedReason={lockedReason(i)}
            processing={processing === step.id}
            notice={notices[step.id]}
            onFieldChange={(name, v) => setField(step.id, name, v)}
            onProcess={() => processStep(i)}
            onMarkCompleted={() => complete(i, 'manual')}
            onUndo={() => undo(i)}
          />
        ))}
      </div>

      {completedCount === STEPS.length && (
        <div className="xs-all-done" role="status">
          <PartyPopper size={28} /> All {STEPS.length} steps are completed. Reload the page to start a new setup.
        </div>
      )}
    </div>
  );
}
